import { Contact } from '../../../models/Contact';
import { CompanyInitialValues } from '../hooks/useContactAIExtraction';

export interface VcfParseResult {
  contact: Partial<Contact>;
  companyInitialValues?: CompanyInitialValues;
  companyName?: string;
}

/**
 * Decodes bytes trying UTF-8 first, falling back to Windows-1252.
 * Relies on TextDecoder's non-fatal mode producing U+FFFD for invalid UTF-8.
 */
export function decodeWithFallback(bytes: Uint8Array): string {
  const utf8Text = new TextDecoder('utf-8').decode(bytes);
  return utf8Text.includes('\uFFFD')
    ? new TextDecoder('windows-1252').decode(bytes)
    : utf8Text;
}

/**
 * Decodes a Quoted-Printable encoded string.
 */
function decodeQuotedPrintable(str: string): string {
  const decoded = str.replace(/=([0-9A-Fa-f]{2})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );
  try {
    const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
    return decodeWithFallback(bytes);
  } catch {
    return decoded;
  }
}

/**
 * Parses a VCF (vCard) string and extracts contact + company data.
 */
export function parseVcf(vcfContent: string): VcfParseResult {
  const contact: Partial<Contact> = {};
  let companyName: string | undefined;
  let companyStreet: string | undefined;
  let companyZip: string | undefined;
  let companyCity: string | undefined;
  let companyPhone: string | undefined;
  let companyWebsite: string | undefined;

  // Unfold continuation lines (RFC 2425: line starting with space/tab continues previous line)
  const unfolded = vcfContent.replace(/\r?\n[ \t]/g, '');
  const lines = unfolded.split(/\r?\n/);

  for (const line of lines) {
    // Split on first colon to get property and value
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;

    const propertyPart = line.substring(0, colonIndex).toUpperCase();
    let value = line.substring(colonIndex + 1).trim();

    if (!value) continue;

    // Extract the base property name (before any ;TYPE= params)
    const baseProp = propertyPart.split(';')[0];
    const params = propertyPart.toUpperCase();

    // Decode Quoted-Printable if specified
    if (params.includes('ENCODING=QUOTED-PRINTABLE')) {
      value = decodeQuotedPrintable(value.replace(/=$/g, ''));
    }

    switch (baseProp) {
      case 'N': {
        // N:LastName;FirstName;MiddleName;Prefix;Suffix
        const parts = value.split(';');
        if (parts[0]) contact.lastName = parts[0].trim();
        if (parts[1]) contact.firstName = parts[1].trim();
        if (parts[3]) contact.title = parts[3].trim();
        break;
      }
      case 'FN': {
        // Full Name — only use as fallback if N is not present
        if (!contact.firstName && !contact.lastName) {
          const parts = value.split(' ');
          if (parts.length >= 2) {
            contact.firstName = parts.slice(0, -1).join(' ');
            contact.lastName = parts[parts.length - 1];
          } else {
            contact.lastName = value;
          }
        }
        break;
      }
      case 'EMAIL': {
        if (!contact.eMail) {
          contact.eMail = value;
        } else if (!contact.eMail2) {
          contact.eMail2 = value;
        }
        break;
      }
      case 'TEL': {
        if (params.includes('CELL') || params.includes('MOBILE')) {
          if (!contact.cellphone) {
            contact.cellphone = value;
          } else if (!contact.cellphone2) {
            contact.cellphone2 = value;
          }
        } else if (params.includes('FAX')) {
          if (!contact.fax) contact.fax = value;
        } else if (params.includes('WORK')) {
          // Work phone -> could be company phone or personal work phone
          if (!contact.phone) {
            contact.phone = value;
          } else if (!contact.phone2) {
            contact.phone2 = value;
          }
        } else {
          if (!contact.phone) {
            contact.phone = value;
          } else if (!contact.phone2) {
            contact.phone2 = value;
          }
        }
        break;
      }
      case 'ORG': {
        // ORG:CompanyName;Department
        const parts = value.split(';');
        if (parts[0]) companyName = parts[0].trim();
        if (parts[1]) contact.department = parts[1].trim();
        break;
      }
      case 'TITLE': {
        contact.position = value;
        break;
      }
      case 'ROLE': {
        if (!contact.position) contact.position = value;
        break;
      }
      case 'ADR': {
        // ADR:PO Box;Extended;Street;City;Region;Postal Code;Country
        const parts = value.split(';');
        const street = parts[2]?.trim();
        const city = parts[3]?.trim();
        const zip = parts[5]?.trim();
        if (street) companyStreet = street;
        if (city) companyCity = city;
        if (zip) companyZip = zip;
        break;
      }
      case 'URL': {
        companyWebsite = value;
        break;
      }
      case 'NOTE': {
        contact.notes = value.replace(/\\n/g, '\n');
        break;
      }
      case 'X-GENDER':
      case 'GENDER': {
        const g = value.toUpperCase();
        if (g.startsWith('M')) contact.gender = 'male' as any;
        else if (g.startsWith('F')) contact.gender = 'female' as any;
        break;
      }
    }
  }

  // Build company initial values if company name found
  let companyInitialValues: CompanyInitialValues | undefined;
  if (companyName) {
    companyInitialValues = {
      shortName: companyName,
      fullName: companyName,
      ...(companyWebsite ? { website: companyWebsite } : {}),
      offices: [
        {
          ...(companyStreet ? { street: companyStreet } : {}),
          ...(companyZip ? { zip: companyZip } : {}),
          ...(companyCity ? { city: companyCity } : {}),
          ...(companyPhone ? { phone: companyPhone } : {}),
          eMail: '',
          officeType: 'externalOffice',
        },
      ],
    };
  }

  return { contact, companyInitialValues, companyName };
}
